import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Printer, Loader2, Download, Plus, Trash2, Ship } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import SeaFreightCertificatePrint from './SeaFreightCertificatePrint';

const DOC_TYPE = 'SEA_FREIGHT_CERTIFICATE';

const EMPTY_CONTAINER = { container_no: '', size: '' };

const EMPTY_FORM = {
  job_no: '', date: '',
  shipper: '', consignee: '', notify: '',
  shipping_line: '', vessel_voyage: '', vessel_date: '', port_of_load: '',
  port_of_discharge: '', final_destination: '',
  mbl_no: '', mbl_date: '', cbm: '',
  hbl_no: '', hbl_date: '', no_of_packages_hbl: '', gross_weight_hbl: '', hbl_description: '',
  containers: [{ ...EMPTY_CONTAINER }],
  igm_no: '', igm_date: '', temperature: '',
  description: '', quantity: '', currency: '', amount: '', total_amount: '',
};

const buildPartyText = (p) => {
  if (!p) return '';
  return [p.name, p.branch_name, p.address,
    p.phone ? `Tel: ${p.phone}` : '',
    p.email ? `Email: ${p.email}` : '']
    .filter(Boolean).join('\n');
};

const SeaFreightCertificateForm = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const printRef = useRef(null);

  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [isSaved, setIsSaved]       = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [docketInfo, setDocketInfo] = useState(null);
  const [showPrint, setShowPrint]   = useState(false);
  const [formData, setFormData]     = useState(EMPTY_FORM);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    if (!docketId) return;
    (async () => {
      try {
        setLoading(true);
        const dr = await fetch(`${backendUrl}/api/dockets/${docketId}/print-data`, { headers: getAuth() });
        if (!dr.ok) throw new Error('Failed to fetch docket');
        const docketData = await dr.json();
        setDocketInfo(docketData);
        if (docketData.company) setCompanyData(docketData.company);

        // Check existing record
        const docRes = await fetch(`${backendUrl}/api/dockets/${docketId}/documents/${DOC_TYPE}`, { headers: getAuth() });
        if (docRes.ok) {
          const docData = await docRes.json();
          if (docData?.id) {
            setFormData(docData.document_data);
            setDocumentId(docData.id);
            setIsSaved(true);
            return;
          }
        }
        populateFromDocket(docketData);
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, [docketId]);

  const populateFromDocket = (docketData) => {
    const { docket, resolved_parties, resolved_shipment } = docketData;
    const payload      = docket.payload_json || {};
    const shipmentInfo = payload.shipment_info || {};
    const invoices     = payload.invoices || [];
    const invoice      = invoices[0] || {};
    const containerDetails = payload.container_details || [];

    const containers = containerDetails.length > 0
      ? containerDetails.map(c => ({ container_no: c.container_no || '', size: c.container_type || c.size || '' }))
      : [{ ...EMPTY_CONTAINER }];

    setFormData({
      job_no:            docket.system_docket_no || '',
      date:              new Date().toISOString().split('T')[0],
      shipper:           buildPartyText(resolved_parties?.exporter_shipper),
      consignee:         buildPartyText(resolved_parties?.importer_receiver),
      notify:            buildPartyText(resolved_parties?.notify_party),
      shipping_line:     resolved_shipment?.carrier || '',
      vessel_voyage:     shipmentInfo.flight_no || '',
      vessel_date:       shipmentInfo.flight_date || '',
      port_of_load:      resolved_shipment?.loading_from || '',
      port_of_discharge: resolved_shipment?.discharge_at || '',
      final_destination: shipmentInfo.delivery_at || '',
      mbl_no:            shipmentInfo.mawb_no || '',
      mbl_date:          shipmentInfo.mawb_date || '',
      cbm:               invoice.cbm_chargeable_weight || '0.00',
      hbl_no:            shipmentInfo.hawb_no || '',
      hbl_date:          shipmentInfo.hawb_date || '',
      no_of_packages_hbl: invoice.no_of_packages || '',
      gross_weight_hbl:  invoice.gross_weight || '',
      hbl_description:   invoice.description || '',
      containers,
      igm_no:            shipmentInfo.igm_no || '',
      igm_date:          shipmentInfo.igm_date || '',
      temperature:       payload.header?.temperature || '',
      description:       invoice.description || '',
      quantity:          '', currency: '', amount: '', total_amount: '',
    });
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const updateContainer = (idx, field, value) => {
    const updated = formData.containers.map((r, i) => i === idx ? { ...r, [field]: value } : r);
    setFormData(prev => ({ ...prev, containers: updated }));
  };
  const addContainer    = () => setFormData(prev => ({ ...prev, containers: [...prev.containers, { ...EMPTY_CONTAINER }] }));
  const removeContainer = (idx) => {
    if (formData.containers.length <= 1) return;
    setFormData(prev => ({ ...prev, containers: prev.containers.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const url    = isSaved
        ? `${backendUrl}/api/dockets/${docketId}/documents/${DOC_TYPE}`
        : `${backendUrl}/api/dockets/${docketId}/documents`;
      const method = isSaved ? 'PUT' : 'POST';
      const body   = isSaved
        ? { document_data: formData }
        : { document_type: DOC_TYPE, document_data: formData };

      const res = await fetch(url, { method, headers: getAuth(), body: JSON.stringify(body) });
      if (!res.ok) {
        const err    = await res.json();
        const detail = Array.isArray(err.detail) ? err.detail.map(e => e.msg).join(', ') : err.detail || 'Save failed';
        throw new Error(detail);
      }
      const saved = await res.json();
      setDocumentId(saved.id);
      setIsSaved(true);
      toast({ title: 'Success', description: 'SEA Freight Certificate saved successfully' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const orig = document.title;
    document.title = `SEA_FC_${formData.job_no || docketId}`;
    setShowPrint(true);
    setTimeout(() => { window.print(); setTimeout(() => { document.title = orig; }, 1000); }, 300);
  };

  if (showPrint) {
    return (
      <div className="min-h-screen bg-white">
        <div className="print:hidden fixed top-4 left-4 right-4 z-50 flex justify-between">
          <Button variant="outline" onClick={() => { document.title = 'LogisticsHub'; setShowPrint(false); }} className="bg-white shadow-md">
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()} className="bg-white shadow-md">
              <Download className="w-4 h-4 mr-2" />Save PDF
            </Button>
            <Button onClick={() => window.print()} className="bg-teal-600 hover:bg-teal-700 shadow-md">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
          </div>
        </div>
        <div className="pt-16">
          <SeaFreightCertificatePrint ref={printRef} docketId={docketId} formData={formData} companyData={companyData} />
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  const F = ({ label, field, inputType = 'text', readOnly = false, placeholder = '' }) => (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <Input type={inputType} value={formData[field] || ''} onChange={e => set(field, e.target.value)}
        readOnly={readOnly} placeholder={placeholder}
        className={`mt-1 h-9 text-sm ${readOnly ? 'bg-gray-50' : ''}`} data-testid={`sea-fc-${field}`} />
    </div>
  );

  const T = ({ label, field, rows = 3 }) => (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <Textarea value={formData[field] || ''} onChange={e => set(field, e.target.value)}
        className="mt-1 text-sm" rows={rows} data-testid={`sea-fc-${field}`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Slim title bar */}
      <div className="border-b border-gray-200 bg-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" />Back
          </Button>
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <Ship className="w-4 h-4 text-teal-600" />Sea Freight Certificate
          </span>
        </div>
        <div className="flex gap-2">
          {isSaved && (
            <Button variant="outline" size="sm" onClick={handlePrint} className="border-teal-500 text-teal-600 hover:bg-teal-50">
              <Printer className="w-3.5 h-3.5 mr-1.5" />Print / Download
            </Button>
          )}
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-5">
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-5">

          {/* Job No + Date */}
          <div className="grid grid-cols-2 gap-4">
            <F label="Job No." field="job_no" readOnly />
            <F label="Date" field="date" inputType="date" />
          </div>

          {/* Shipper | Consignee | Notify */}
          <div className="grid grid-cols-3 gap-4">
            <T label="Shipper*" field="shipper" />
            <T label="Consignee" field="consignee" />
            <T label="Notify" field="notify" />
          </div>

          {/* Shipping Line | Vessel/Voyage | Date | Port of Load */}
          <div className="grid grid-cols-4 gap-4">
            <F label="Shipping line" field="shipping_line" />
            <F label="Vessel/Voyage" field="vessel_voyage" />
            <F label="Date" field="vessel_date" inputType="date" />
            <F label="Port of Load" field="port_of_load" />
          </div>

          {/* Port of Discharge | Final Place of destination */}
          <div className="grid grid-cols-4 gap-4">
            <F label="Port of Discharge" field="port_of_discharge" />
            <F label="Final Place of destination" field="final_destination" />
          </div>

          {/* MBL No | MBL Date | CBM */}
          <div className="grid grid-cols-3 gap-4">
            <F label="MBL No." field="mbl_no" />
            <F label="MBL Date" field="mbl_date" inputType="date" />
            <F label="CBM" field="cbm" inputType="number" placeholder="0.00" />
          </div>

          {/* HBL No | HBL Date | No. of packages HBL | Gross weight HBL | Description */}
          <div className="grid grid-cols-5 gap-4 items-start">
            <F label="HBL No." field="hbl_no" />
            <F label="Date" field="hbl_date" inputType="date" />
            <F label="No. of packages HBL" field="no_of_packages_hbl" />
            <F label="Gross weight HBL" field="gross_weight_hbl" />
            <T label="Description" field="hbl_description" rows={3} />
          </div>

          {/* Container rows (No. + Size only) */}
          <div className="space-y-2">
            {formData.containers.map((row, idx) => (
              <div key={idx} className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <Label className="text-xs text-gray-500">Container No.</Label>
                  <Input value={row.container_no} onChange={e => updateContainer(idx, 'container_no', e.target.value)}
                    className="mt-1 h-9 text-sm" placeholder="e.g. MSKU1234567" data-testid="sea-fc-container-no" />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Size</Label>
                    <Input value={row.size} onChange={e => updateContainer(idx, 'size', e.target.value)}
                      className="mt-1 h-9 text-sm" placeholder="e.g. 20 Feet Container" data-testid="sea-fc-size" />
                  </div>
                  {formData.containers.length > 1 && (
                    <button onClick={() => removeContainer(idx)} className="mb-0.5 text-red-400 hover:text-red-600 p-1" data-testid="sea-fc-remove-container">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addContainer}
              className="border-teal-500 text-teal-600 hover:bg-teal-50 text-xs mt-1" data-testid="sea-fc-add-container">
              <Plus className="w-3.5 h-3.5 mr-1" />Add Container
            </Button>
          </div>

          {/* IGM No | IGM Date | Temperature */}
          <div className="grid grid-cols-3 gap-4">
            <F label="IGM No." field="igm_no" />
            <F label="Date" field="igm_date" inputType="date" />
            <F label="Temperature" field="temperature" placeholder="e.g. 2-8°C" />
          </div>

          {/* Description | Quantity | Currency | Amount | Total Amount */}
          <div className="grid grid-cols-5 gap-4 items-start">
            <T label="Description" field="description" rows={3} />
            <F label="Quantity" field="quantity" inputType="number" />
            <F label="Currency" field="currency" placeholder="e.g. USD" />
            <F label="Amount" field="amount" inputType="number" />
            <F label="Total Amount" field="total_amount" inputType="number" />
          </div>

        </Card>

        {isSaved && (
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm text-teal-700"><span className="font-semibold">Saved</span> — Linked to docket {docketInfo?.docket?.system_docket_no}</p>
            <span className="text-xs text-teal-500">ID: {documentId?.slice(0, 8)}...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeaFreightCertificateForm;
